import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Menu, X, Phone, ArrowLeftRight } from "lucide-react";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = ({ restaurant, other }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const anchors = [
    { id: "poveste", label: "Poveste", testid: "nav-link-about" },
    { id: "meniu", label: "Meniu", testid: "nav-link-menu" },
    { id: "galerie", label: "Galerie", testid: "nav-link-gallery" },
    { id: "rezervari", label: "Rezervări", testid: "nav-link-reservation" },
    { id: "contact", label: "Contact", testid: "nav-link-contact" },
  ];

  const go = (id) => {
    setOpen(false);
    if (isHome) {
      scrollToId("restaurante");
      toast("Mai întâi alege casa, pofticiosule!", {
        description: "Meniurile, galeriile și rezervările sunt la fiecare restaurant în parte. Alege mai jos: La Zoo (românesc) sau La Unirii (pizza & paste).",
        duration: 5000,
      });
    } else {
      scrollToId(id);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#121110]/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <img
            src="/assets/logo-zoo.jpg"
            alt="Nea Mitică"
            className="h-10 w-10 rounded-full object-cover ring-2 ring-red-600/70 transition-transform duration-500 group-hover:rotate-[8deg]"
          />
          <span className="font-display text-lg font-bold tracking-tight text-[#FAF6F0]">
            Nea Mitică
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {anchors.map((a) => (
            <button
              key={a.id}
              data-testid={a.testid}
              onClick={() => go(a.id)}
              className="text-sm font-medium text-stone-300 transition-colors hover:text-red-400"
            >
              {a.label}
            </button>
          ))}
          {other && (
            <button
              data-testid="nav-switch-location"
              onClick={() => navigate(`/${other.id}`)}
              className="flex items-center gap-2 rounded-full border border-red-600/50 px-4 py-1.5 text-xs font-semibold text-red-400 transition-colors hover:bg-red-600 hover:text-white"
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
              {other.short_name}
            </button>
          )}
          {restaurant && (
            <a
              href={restaurant.phone_link}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
            >
              <Phone className="h-4 w-4" />
              {restaurant.phone}
            </a>
          )}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="text-stone-200 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Meniu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#121110]/95 px-5 py-4 backdrop-blur-xl lg:hidden">
          {anchors.map((a) => (
            <button
              key={a.id}
              data-testid={`${a.testid}-mobile`}
              onClick={() => go(a.id)}
              className="block w-full py-2.5 text-left text-base text-stone-200"
            >
              {a.label}
            </button>
          ))}
          {other && (
            <button
              data-testid="nav-switch-location-mobile"
              onClick={() => { setOpen(false); navigate(`/${other.id}`); }}
              className="mt-2 flex items-center gap-2 text-sm font-semibold text-red-400"
            >
              <ArrowLeftRight className="h-4 w-4" /> Mergi {other.short_name}
            </button>
          )}
        </div>
      )}
    </motion.header>
  );
};
